import { FeatureFlags } from '../FeatureFlags';
import { CommonErrorCode } from '../helpers/ErrorCodes';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { Module } from '../Module';
import { ServerUsers } from '../../userManagement/services/resources/ServerUsers';
import { CreatePersonalAccessTokenRequest, CreatePersonalAccessTokenResult } from '../ServerApi/requests/CreatePersonalAccessTokenRequest';
import { IConnectedDevice } from '../ServerApi/types/IConnectedDevice';
import { IPersonalAccessToken } from '../ServerApi/types/IPersonalAccessToken';
import { ModalService} from './ModalService';
import { ToasterService, ToastType } from './ToasterService';
import { showDeletePersonalAccessTokenActionDialog } from '../../authentication/services/DeletePersonalAccessTokenAction';
import WebHttpClient from '../../httpClient/WebHttpClient';
import { ReactModalService } from '../../commonInfra/dialogs/ReactModalService';
import {
  DialogPositioningBehavior,
  DialogType,
  DialogWidget, TextFieldProps, TextFieldWidget,
} from '@tableau/widgets';
import { TabDialogWidth} from '../../commonInfra/dialogs/style/TabDialogWidth';
import * as React from 'react';
import getDefaultDialogProps = ReactModalService.getDefaultDialogProps;
import {
  CreatePersonalAccessTokenDialogProps, GetDialogWidget, GetFooterContent
} from '../../authentication/components/CreatePersonalAccessTokenDialog';
import {Messages} from '../../codegen/Localize';
import {
  internalTextFieldStyle,
  successStyle,
  textWidgetStyle,
  warningStyle
} from '../../authentication/styles/CreatePersonalAccessTokenDialogStyle';


export interface PersonalAccessToken {
  tokenName: string;
  tokenSecret: string;
}

export interface PersonalAccessTokensScope extends ng.IScope {
    // Create New Token
    controls: {personalAccessTokenName: string};
    createNewPersonalAccessToken: () => void;
    isPersonalAccessTokenNameEmpty: () => boolean;
    isPersonalAccessTokenNameUsed: () => boolean;
    isCreateNewPersonalAccessTokenDisabled: () => boolean;
    canCreatePersonalAccessTokens: boolean;

    // List All Tokens
    connectedPersonalAccessTokens: IPersonalAccessToken[];
    deletePersonalAccessToken(token: IPersonalAccessToken, $event: JQueryEventObject): void;

    connectedDevices: IConnectedDevice[];
}



export class PersonalAccessTokensService {

  public static DEVICE_NAME_FOR_TOKENS = 'PERSONAL_ACCESS_TOKEN';
  private modalService: ModalService;
  private $translate: ng.translate.ITranslateService;
  private toaster: ToasterService;

  public static $inject = [
    'ModalService',
    '$translate'
  ];

  constructor(
      modalService: ModalService,
      $translate: ng.translate.ITranslateService,
      ) {
    this.modalService = modalService;
    this.$translate = $translate;
  }

  public loadTokensIntoScope(scope: PersonalAccessTokensScope, serverUser: IServerUserInfo): () => void {
    // If the feature is disabled, we leave the scope as is.
    if (!FeatureFlags.personalAccessTokensEnabled) {
        return () => {}; // Empty callback
     }

    const refreshTokensAndDevices = () => {
      // Refresh Personal Access Tokens
      ServerUsers.instance.getPersonalAccessTokens(serverUser.id)
        .then((tokens: IPersonalAccessToken[]) => scope.connectedPersonalAccessTokens = tokens);

      if (scope.connectedDevices) {
        // Removing the Personal Access Tokens device from the Connected Clients list.
        scope.connectedDevices = scope.connectedDevices.filter(x => x.id !== PersonalAccessTokensService.DEVICE_NAME_FOR_TOKENS);
      }
    };
    return refreshTokensAndDevices;
  }

  public loadActionsIntoScope(scope: PersonalAccessTokensScope, serverUser: IServerUserInfo, isSessionUserSelf: boolean,
    refreshDevicesAndTokens: () => void) {
    if (!FeatureFlags.personalAccessTokensEnabled) {
        return;
    }

    // Create tokens should only appear on the personal settings page, since we are always creating tokens for oneself.
    scope.canCreatePersonalAccessTokens = isSessionUserSelf;
    scope.createNewPersonalAccessToken = () => this.createNewPersonalAccessToken(scope, refreshDevicesAndTokens);

    scope.isPersonalAccessTokenNameEmpty = () => {
      const name = scope.controls.personalAccessTokenName;
      return !name || name.length === 0;
    };

    scope.isPersonalAccessTokenNameUsed = () => {
      const name = scope.controls.personalAccessTokenName;
      const tokens = scope.connectedPersonalAccessTokens;
      return tokens && tokens.some(x => x.clientId === name);
    };

    scope.isCreateNewPersonalAccessTokenDisabled = () => {
      return scope.isPersonalAccessTokenNameEmpty() || scope.isPersonalAccessTokenNameUsed();
    };

    scope.deletePersonalAccessToken = (token: IPersonalAccessToken, $event: JQueryEventObject) =>
      showDeletePersonalAccessTokenActionDialog(token, serverUser.id)
        .then(refreshDevicesAndTokens);
  }

  private createNewPersonalAccessToken = (scope: PersonalAccessTokensScope, refreshDevicesAndTokens: () => void) => {
    WebHttpClient.sendRequest(new CreatePersonalAccessTokenRequest({
        clientId: scope.controls.personalAccessTokenName
    })).then((result: CreatePersonalAccessTokenResult) => {
      if (result && result.refreshToken) {
        this.openNewTokenDialog(scope, result.refreshToken, refreshDevicesAndTokens);
      } else {
        this.processCreateTokenError(result);
      }
    });
  }

  private processCreateTokenError(result: CreatePersonalAccessTokenResult) {
    let errorMessageKey = 'errorUnknown';
    if (result && result.errors && result.errors.length) {
      switch (result.errors[0].code) {
        case CommonErrorCode.MaximumRefreshTokenReached:
          errorMessageKey = 'personalAccessTokensMaximumLimitReached';
      }
    }
    this.toaster.pop({ type: ToastType.Error, text: this.$translate.instant(errorMessageKey) });
  }

  private openNewTokenDialog(scope: PersonalAccessTokensScope, refreshToken: string, refreshDevicesAndTokens: () => void) {
    const token: PersonalAccessToken = {
      tokenName: scope.controls.personalAccessTokenName,
      tokenSecret: refreshToken,
    };

    const modalInstance = ReactModalService.openCustom(closeAndDismiss => {
      const props: CreatePersonalAccessTokenDialogProps = {
        token: token,
        dismiss: closeAndDismiss.dismiss
      };
      const dialogProps = getDefaultDialogProps({
        type: DialogType.NormalWithTitleBar,
        title: Messages.personalAccessTokenDialogTitle(),
        testId: 'createPersonalAccessTokenDialog',
        showCloseButton: true,
        positioningBehavior: DialogPositioningBehavior.Centered,
        dialogWidth: TabDialogWidth.width500px,
        footerContent: GetFooterContent(props)
      }, closeAndDismiss);

      return GetDialogWidget(dialogProps, token);
    });

    modalInstance.result.catch(() => {
      scope.$apply(() => scope.controls.personalAccessTokenName = '');
    });
    modalInstance.result.finally(refreshDevicesAndTokens);

  }
}

Module.instance().service('PersonalAccessTokensService', PersonalAccessTokensService);
